// Ported from memex-app's Insights theme (std-27): the ONE Nivo theme + chart
// palette every chart shares, so Backstage's growth charts read identically to
// the product's own charts — same hues, same translucent-fill-with-crisp-edge
// treatment, same theme-aware tokens. Kept deliberately close to the source so a
// future divergence is a visible diff, not silent drift.
//
// Series colors are literal hex (Nivo composes colors in JS — `${accent}55`
// alpha suffixes — where a `rgb(var(--…))` string would break). The chrome
// (grid, axes, tooltip) uses the app's `rgb(var(--token))` design tokens so it
// tracks light/dark.

import type { CSSProperties } from "react";
import type { PartialTheme } from "@nivo/theming";
import { useThemeName } from "../ThemeContext";

export const PHASE_ORDER = ["draft", "specify", "build", "verify", "done"] as const;
export type Phase = (typeof PHASE_ORDER)[number];

export interface ChartPalette {
  /** Cumulative / "intent" series. */
  accent: string;
  /** Distinct second cumulative hue (so two lines never collide). */
  human: string;
  verified: string;
  failing: string;
  // Phase hues (std-27): draft = slate, specify = amber, build = blue,
  // verify = cyan, done = emerald — for the spec-phase stacked bar.
  phase: Record<Phase, string>;
}

export const CHART_PALETTES: Record<"dark" | "light", ChartPalette> = {
  dark: {
    accent: "#a78bfa", // violet-400
    human: "#60a5fa", // blue-400
    verified: "#34d399", // emerald-400
    failing: "#fb7185", // rose-400
    phase: {
      draft: "#64748b", // slate-500
      specify: "#fbbf24", // amber-400
      build: "#60a5fa", // blue-400
      verify: "#22d3ee", // cyan-400
      done: "#34d399", // emerald-400
    },
  },
  light: {
    accent: "#8b5cf6", // violet-500
    human: "#3b82f6", // blue-500
    verified: "#10b981", // emerald-500
    failing: "#f43f5e", // rose-500
    phase: {
      draft: "#94a3b8", // slate-400
      specify: "#f59e0b", // amber-500
      build: "#3b82f6", // blue-500
      verify: "#0891b2", // cyan-600
      done: "#10b981", // emerald-500
    },
  },
};

/** The chart palette for the active theme (defaults to dark outside the provider). */
export function useChartPalette(): ChartPalette {
  return CHART_PALETTES[useThemeName()];
}

// Category-heavy charts (the funnel's 9 activation stages, Sankey nodes) need MANY
// hues that are unmistakably distinct — the 6 semantic series colors above are too
// few and too close (violet↔blue, emerald↔cyan) and repeat past 6 (spec-13 dec-8).
// This is a HAND-PICKED, deliberately VIBRANT set (NOT an off-the-shelf @nivo
// scheme, several of which are pastel): 10 high-saturation hues spread around the
// wheel so every stage/node reads as its own colour. Dark gets the lighter -400
// tints, light gets the deeper -500/-600 tones, matching the chrome's contrast.
export const VIBRANT_CATEGORICAL: Record<"dark" | "light", string[]> = {
  dark: [
    "#f87171", // red-400
    "#fb923c", // orange-400
    "#fbbf24", // amber-400
    "#a3e635", // lime-400
    "#34d399", // emerald-400
    "#22d3ee", // cyan-400
    "#60a5fa", // blue-400
    "#818cf8", // indigo-400
    "#c084fc", // violet-400
    "#f472b6", // pink-400
  ],
  light: [
    "#ef4444", // red-500
    "#f97316", // orange-500
    "#f59e0b", // amber-500
    "#84cc16", // lime-500
    "#10b981", // emerald-500
    "#06b6d4", // cyan-500
    "#3b82f6", // blue-500
    "#6366f1", // indigo-500
    "#a855f7", // violet-500
    "#ec4899", // pink-500
  ],
};

/** The vibrant categorical palette for category-heavy charts in the active theme. */
export function useVibrantPalette(): string[] {
  return VIBRANT_CATEGORICAL[useThemeName()];
}

// Heatmap intensity wants a single-hue SEQUENTIAL ramp that stays SATURATED at the
// high end (a washed-out light ramp hides the busy cells). Endpoints feed Nivo's
// sequential color config; we keep a low→high blue/cyan ramp vivid at the top in
// both themes.
export const SEQUENTIAL_RAMP: Record<"dark" | "light", [string, string]> = {
  dark: ["#1e293b", "#22d3ee"], // slate-800 → cyan-400
  light: ["#e0f2fe", "#0369a1"], // sky-100 → sky-700
};

/** The saturated sequential ramp [low, high] for heatmap intensity. */
export function useSequentialRamp(): [string, string] {
  return SEQUENTIAL_RAMP[useThemeName()];
}

// The app's design tokens (index.css) are space-separated RGB *channels*
// (`--color-edge: 62 68 81`), consumed via `rgb(var(--token))`. Hairlines use the
// edge token at 0.5 alpha — subtle in both themes, with a semi-transparent hex
// fallback so a missing token can never produce a hard line.
const HAIRLINE = "rgb(var(--color-edge, 148 163 184) / 0.5)";

export const insightsTheme: PartialTheme = {
  background: "transparent",
  text: {
    fontSize: 11,
    fill: "rgb(var(--color-text-secondary, 100 116 139))",
  },
  axis: {
    ticks: {
      line: { stroke: HAIRLINE, strokeWidth: 1 },
      text: { fontSize: 10, fill: "rgb(var(--color-text-secondary, 100 116 139))" },
    },
    legend: { text: { fontSize: 11, fill: "rgb(var(--color-text-secondary, 100 116 139))" } },
  },
  grid: {
    line: { stroke: HAIRLINE, strokeWidth: 1, strokeDasharray: "2 4" },
  },
  tooltip: {
    container: {
      background: "rgb(var(--color-surface, 255 255 255))",
      color: "rgb(var(--color-text-primary, 15 23 42))",
      fontSize: 12,
      borderRadius: 8,
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
      padding: "8px 12px",
    },
  },
  crosshair: {
    line: { stroke: "rgb(var(--color-text-secondary, 148 163 184))", strokeWidth: 1, strokeOpacity: 0.5 },
  },
};

/** Shared inline style for custom tooltip divs (Nivo's theme.tooltip doesn't
 * reach slice tooltips that replace the container entirely). */
export const TOOLTIP_STYLE: CSSProperties = {
  background: "rgb(var(--color-surface, 255 255 255))",
  color: "rgb(var(--color-text-primary, 15 23 42))",
  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
};

/**
 * ≤6 evenly-spaced INTEGER ticks for count axes. Nivo's default linear ticks
 * emit 0.5/1.5/… which reads as nonsense for user/memex counts — pass these to
 * both tickValues and gridYValues.
 */
export function integerTicks(max: number): number[] {
  const top = Math.max(1, Math.ceil(max));
  const step = Math.max(1, Math.ceil(top / 5));
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  return ticks;
}
